import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';

export default function ResetPassword() {
  const { token } = useParams();
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`/api/auth/reset-password/${token}`, { password: form.password });
      if (res.data?.token) {
        localStorage.setItem('dwi_token', res.data.token);
      }
      setDone(true);
      toast.success('Password reset! Redirecting...');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-xl text-white"
              style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}>D</div>
            <div className="text-left">
              <p className="font-black text-text-primary text-xl tracking-tight">DWI FINTECH</p>
              <p className="text-text-muted text-xs">Set New Password</p>
            </div>
          </div>
        </div>

        <div className="bg-bg-card border border-border rounded-2xl p-8 shadow-2xl">
          {done ? (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green/10 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-text-primary font-bold text-xl mb-2">Password Reset!</h2>
              <p className="text-text-secondary text-sm">Redirecting to your dashboard...</p>
            </div>
          ) : (
            <>
              <h2 className="text-text-primary font-bold text-2xl mb-1">Set New Password</h2>
              <p className="text-text-secondary text-sm mb-6">Choose a strong password for your account.</p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-text-secondary text-sm font-medium mb-2">New Password</label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                    placeholder="Minimum 6 characters"
                    required
                    className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted"
                  />
                </div>
                <div>
                  <label className="block text-text-secondary text-sm font-medium mb-2">Confirm Password</label>
                  <input
                    type="password"
                    value={form.confirm}
                    onChange={(e) => setForm(f => ({ ...f, confirm: e.target.value }))}
                    placeholder="Repeat new password"
                    required
                    className="w-full bg-bg border border-border rounded-xl px-4 py-3 text-text-primary text-sm outline-none focus:border-cyan transition-colors placeholder:text-text-muted"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl font-bold text-white text-sm transition-opacity disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, #00A8DB, #8B5CF6)' }}
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/login" className="text-text-muted text-sm hover:text-cyan transition-colors">← Back to Login</Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
